import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Auth,
  browserLocalPersistence,
  browserSessionPersistence,
  ConfirmationResult,
  getIdToken,
  GoogleAuthProvider,
  OAuthProvider,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut
} from '@angular/fire/auth';
import { from, map, Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { SessionStorageService } from './session-storage.service';
import { PortalAccessOption } from './portal.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly http = inject(HttpClient);
  private readonly storage = inject(SessionStorageService);
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;
  private whatsAppOtpChallengeId: string | null = null;

  get usesWhatsAppOtp(): boolean {
    return environment.whatsAppOtpEnabled;
  }

  login(email: string, password: string, rememberMe: boolean): Observable<void> {
    const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
    return from(setPersistence(this.auth, persistence)).pipe(
      switchMap(() => from(signInWithEmailAndPassword(this.auth, email, password))),
      switchMap(userCredential => from(getIdToken(userCredential.user, true))),
      tap(token => {
        this.storage.saveAccessToken(token);
        this.storage.clearPortalScope();
      }),
      map(() => undefined)
    );
  }

  loginWithGoogle(rememberMe: boolean): Observable<void> {
    return this.loginWithProvider(new GoogleAuthProvider(), rememberMe);
  }

  loginWithMicrosoft(rememberMe: boolean): Observable<void> {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({ prompt: 'select_account' });
    return this.loginWithProvider(provider, rememberMe);
  }

  sendPasswordReset(email: string): Observable<void> {
    return from(sendPasswordResetEmail(this.auth, email));
  }

  initializeRecaptcha(containerId: string): void {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }
    this.recaptchaVerifier = new RecaptchaVerifier(this.auth, containerId, {
      size: 'invisible'
    });
  }

  sendPhoneOtp(phoneNumber: string): Observable<void> {
    if (this.usesWhatsAppOtp) {
      return this.sendWhatsAppOtp(phoneNumber);
    }
    if (!this.recaptchaVerifier) {
      throw new Error('Phone sign-in is not ready. Try again in a moment.');
    }
    return from(signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier)).pipe(
      tap(result => this.confirmationResult = result),
      map(() => undefined)
    );
  }

  verifyPhoneOtp(code: string): Observable<void> {
    if (this.usesWhatsAppOtp) {
      return this.verifyWhatsAppOtp(code);
    }
    if (!this.confirmationResult) {
      throw new Error('No phone OTP request is active.');
    }
    return from(this.confirmationResult.confirm(code)).pipe(
      switchMap(userCredential => from(getIdToken(userCredential.user, true))),
      tap(token => {
        this.storage.saveAccessToken(token);
        this.storage.clearPortalScope();
      }),
      map(() => undefined)
    );
  }

  getCurrentUserToken(): Observable<string> {
    return from(this.auth.authStateReady()).pipe(
      switchMap(() => {
        const user = this.auth.currentUser;
        if (!user) {
          throw new Error('No active Firebase user.');
        }
        return from(getIdToken(user, true));
      }),
      tap(token => this.storage.saveAccessToken(token))
    );
  }

  async hasCurrentUser(): Promise<boolean> {
    await this.auth.authStateReady();
    return !!this.auth.currentUser;
  }

  activateSession(): Observable<void> {
    const browserSessionId = this.storage.ensureBrowserSessionId();
    let headers = new HttpHeaders({
      Authorization: `Bearer ${this.storage.getAccessToken() ?? ''}`,
      'X-Cargomate-Session-Id': browserSessionId,
      'X-Cargomate-Session-Client': 'CUSTOMER_PORTAL'
    });
    const scope = this.storage.getPortalScope();
    if (scope.accessId != null) {
      headers = headers.set('X-Cargomate-Portal-Access-Id', String(scope.accessId));
    } else if (scope.tenantCompanyId != null) {
      headers = headers.set('X-Cargomate-Tenant-Company-Id', String(scope.tenantCompanyId));
    }
    return this.http.post<void>(`${environment.restApiUrl}sessions/activate`, {}, { headers });
  }

  logout(): Observable<void> {
    this.storage.clear();
    return from(signOut(this.auth));
  }

  private sendWhatsAppOtp(phoneNumber: string): Observable<void> {
    return this.http.post<{ challengeId: string }>(`${environment.restApiUrl}auth/whatsapp-otp/request`, {
      phoneNumber,
      client: 'CUSTOMER_PORTAL',
      locale: navigator.language
    }).pipe(
      tap(response => this.whatsAppOtpChallengeId = response.challengeId),
      map(() => undefined)
    );
  }

  private verifyWhatsAppOtp(code: string): Observable<void> {
    if (!this.whatsAppOtpChallengeId) {
      throw new Error('No WhatsApp OTP request is active. Request a new code.');
    }
    return this.http.post<{ customToken: string }>(`${environment.restApiUrl}auth/whatsapp-otp/verify`, {
      challengeId: this.whatsAppOtpChallengeId,
      client: 'CUSTOMER_PORTAL',
      code
    }).pipe(
      switchMap(response => from(signInWithCustomToken(this.auth, response.customToken))),
      switchMap(userCredential => from(getIdToken(userCredential.user, true))),
      tap(token => {
        this.whatsAppOtpChallengeId = null;
        this.storage.saveAccessToken(token);
        this.storage.clearPortalScope();
      }),
      map(() => undefined)
    );
  }

  private loginWithProvider(provider: GoogleAuthProvider | OAuthProvider, rememberMe: boolean): Observable<void> {
    const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
    return from(setPersistence(this.auth, persistence)).pipe(
      switchMap(() => from(signInWithPopup(this.auth, provider))),
      switchMap(userCredential => from(getIdToken(userCredential.user, true))),
      tap(token => {
        this.storage.saveAccessToken(token);
        this.storage.clearPortalScope();
      }),
      map(() => undefined)
    );
  }

  setPortalScope(option: PortalAccessOption): void {
    this.storage.savePortalScope({
      accessId: option.accessId,
      tenantCompanyId: option.tenantCompanyId
    });
  }

  clearPortalScope(): void {
    this.storage.clearPortalScope();
  }
}
